import { useT } from '@transifex/react';

import { useCoachAvailability } from '@/modules/rulesEngine';

import { formatNextAppointment } from '../utils/formatNextAppointment';

export type NextAppointmentLabel = {
  /** Ready-to-render "Today/Tomorrow/Wed, Jun 3: …" label, or undefined when the
   * coach has no upcoming slots. */
  label: string | undefined;
  /** True until this coach's availability has loaded — show a skeleton. */
  isLoading: boolean;
};

/**
 * A single coach's "next appointment" line for the matched-coach cards. Reads
 * the provider's availability from the shared rules-engine cache (one fetch per
 * provider) and formats the soonest day + times. Each card calls this with its
 * own coach, so the fetch is colocated with the card and its loading state
 * drives a per-card skeleton.
 */
export function useNextAppointmentLabel(providerUuid: string | undefined): NextAppointmentLabel {
  const t = useT();
  const { availability, isLoading } = useCoachAvailability(providerUuid);

  const next = availability ? formatNextAppointment(availability.timeslots) : undefined;
  const dayLabel = next?.isToday ? t('Today') : next?.isTomorrow ? t('Tomorrow') : next?.dayLabel;
  const label = next ? `${dayLabel}: ${next.times}` : undefined;

  return { label, isLoading };
}
